import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { authPageSaga } from './saga';
import {
  AuthPageState,
  Business,
  JwtPayload,
  RolePermission,
  catalogItemInfo,
} from './types';
import { CustomError } from 'utils/types/success-error-types';
import { errorModal } from 'utils/showSuccessErrorModal';

export const initialState: AuthPageState = {
  isLoggingIn: false,
  isGettingOrgBusinesses: false,
  isUpdatingBusinessName: false,
  // 2FA specific states
  is2FASetupLoading: false,
  is2FAVerifying: false,
  is2FADisabling: false,
  twoFactorSetupData: null,
  authData: {
    id: 0,
    email: '',
    role: '',
    name: '',
    rolePermission: { id: 0, role: '' },
    createdAt: new Date(),
    token: '',
    isLoggedIn: false,
    exp: 0,
    business: {
      id: 0,
      name: '',
    },
  },
  business: {
    id: 0,
    name: '',
    description: '',
    businessAccountId: '',
    createdAt: new Date(),
    sector: '',
    apiKey: '',
    webVerificationToken: '',
    countryCode: 0,
    bizWhatsappNumber: '',
    bizAttributes: '[]',
    bizAttributesType: '[]',
    phoneNumberId: '',
    fbAccessToken: '',
    fbAppId: '',
    webhookUrl: '',
    catalogSheetLink: '',
    isMarkReadMsg: true,
    integrations: null,
  },
  orgBusinesses: [],
  catalogItems: new Map(),
  permission: undefined,
  rolePermissions: [],
};

const slice = createSlice({
  name: 'authPage',
  initialState,
  reducers: {
    signInStart(
      state,
      _action: PayloadAction<{ email: string; password: string }>,
    ) {
      state.isLoggingIn = true;
    },
    signInSuccess(state, action: PayloadAction<JwtPayload>) {
      state.isLoggingIn = false;
      state.authData = action.payload;
      const permission = localStorage.getItem('rolePermission');
      if (permission) {
        try {
          state.permission = JSON.parse(permission);
        } catch {
          /* empty */
        }
      }
    },
    signInFailure(state, action: PayloadAction<CustomError>) {
      state.isLoggingIn = false;
      errorModal(action.payload.errorMessage);
    },
    getOrgBusinessStart(state) {
      state.isGettingOrgBusinesses = true;
    },
    getOrgBusinessSuccess(state, action: PayloadAction<Business[]>) {
      state.isGettingOrgBusinesses = false;
      state.orgBusinesses = action.payload;
    },
    getOrgBusinessFailure(state, action: PayloadAction<CustomError>) {
      state.isGettingOrgBusinesses = false;
      errorModal(action.payload.errorMessage);
    },
    getBusinessTokenStart(
      state,
      _action: PayloadAction<{ businessId: number }>,
    ) {
      state.isLoggingIn = true;
    },
    addNewBusinessStart(state) {
      state.isGettingOrgBusinesses = true;
    },
    deleteBusinessStart(state, _action: PayloadAction<{ businessId: number }>) {
      state.isGettingOrgBusinesses = true;
    },
    deleteBusinessSuccess(state, action: PayloadAction<Business[]>) {
      state.isGettingOrgBusinesses = false;
      state.orgBusinesses = action.payload;
    },
    deleteBusinessFailure(state, action: PayloadAction<CustomError>) {
      state.isGettingOrgBusinesses = false;
      errorModal(action.payload.errorMessage);
    },
    updateBusinessNameStart(
      state,
      _action: PayloadAction<{ businessId: number; name: string }>,
    ) {
      state.isUpdatingBusinessName = true;
    },
    updateBusinessNameSuccess(state, action: PayloadAction<Partial<Business>>) {
      state.isUpdatingBusinessName = false;
      state.orgBusinesses = state.orgBusinesses.map(business =>
        business.id === action.payload.id
          ? { ...business, name: action.payload.name ?? business.name }
          : business,
      );
      if (state.authData.business?.id === action.payload.id) {
        state.authData.business = {
          id: action.payload.id ?? (state.authData.business?.id || 0),
          name: action.payload.name ?? (state.authData.business?.name || ''),
        };
      }
      if (action.payload.id === state.business.id) {
        state.business.name = action.payload.name || state.business.name;
      }
    },
    updateBusinessNameFailure(state, action: PayloadAction<CustomError>) {
      state.isUpdatingBusinessName = false;
      errorModal(action.payload.errorMessage);
    },
    updateJwtTokenStart() {},
    setCatalogItemsSuccess(state, action: PayloadAction<catalogItemInfo[]>) {
      const catalogItems = action.payload.reduce((map, item) => {
        map.set(item.contentId, item);
        return map;
      }, new Map());
      state.catalogItems = catalogItems;
    },
    getBusinessDetails() {},
    getBusinessDetailsSuccess(state, action: PayloadAction<Business>) {
      state.business = action.payload;
      localStorage.setItem('ApiKey', action.payload.apiKey || '');
    },
    getRolePermissionListStart() {},
    getRolePermissionListSuccess(
      state,
      action: PayloadAction<RolePermission[]>,
    ) {
      state.rolePermissions = action.payload;
      const permission = action.payload.find(
        permission => permission.role === state.authData.rolePermission.role,
      );
      state.permission = permission;
      localStorage.setItem('rolePermission', JSON.stringify(permission));
    },
    // 2FA Actions
    enable2FAStart(state) {
      state.is2FASetupLoading = true;
      state.twoFactorSetupData = null;
    },
    enable2FASuccess(
      state,
      action: PayloadAction<{
        secret: string;
        otpauthUrl: string;
        backupCodes: string[];
      }>,
    ) {
      state.is2FASetupLoading = false;
      state.twoFactorSetupData = action.payload;
    },
    enable2FAFailure(state, action: PayloadAction<CustomError>) {
      state.is2FASetupLoading = false;
      state.twoFactorSetupData = null;
      errorModal(action.payload.errorMessage);
    },
    verify2FAStart(state, _action: PayloadAction<{ token: string }>) {
      state.is2FAVerifying = true;
    },
    verify2FASuccess(state) {
      state.is2FAVerifying = false;
      state.twoFactorSetupData = null;
    },
    verify2FAFailure(state, action: PayloadAction<CustomError>) {
      state.is2FAVerifying = false;
      errorModal(action.payload.errorMessage);
    },
    disable2FAStart(state, _action: PayloadAction<{ token: string }>) {
      state.is2FADisabling = true;
    },
    disable2FASuccess(state) {
      state.is2FADisabling = false;
    },
    disable2FAFailure(state, action: PayloadAction<CustomError>) {
      state.is2FADisabling = false;
      errorModal(action.payload.errorMessage);
    },
  },
});

export const { actions: authPageActions } = slice;

export const useAuthPageSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectSaga({ key: slice.name, saga: authPageSaga });
  return { actions: slice.actions };
};

/**
 * Example Usage:
 *
 * export function MyComponentNeedingThisSlice() {
 *  const { actions } = useAuthPageSlice();
 *
 *  const onButtonClick = (evt) => {
 *    dispatch(actions.someAction());
 *   };
 * }
 */
